import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { validatePassword, passwordCheckBreakdown } from "../lib/passwordPolicy";
import { Check, X, RefreshCw, AlertTriangle } from "lucide-react";
import WelcomePopup from "../components/WelcomePopup";
import PasswordInput from "../components/PasswordInput";
import api from "../lib/api";
import { suggestEmail } from "../lib/emailTypoSuggest";

const TERMS_VERSION = "2026-02-27";
const TYPES = ["Coiffeur", "Barbier", "Clinique", "Médecin", "Mairie", "Administration", "Association", "Garage", "Restaurant", "Autre"];
const ORG_TYPES = [
  { value: "", label: "Je suis…" },
  { value: "entreprise", label: "Une entreprise" },
  { value: "association", label: "Une association" },
  { value: "administration", label: "Une administration" },
  { value: "autre", label: "Autre" },
];

// Adresse Data Gouv — open French government address API (no key required).
// Returns "features" array with `properties.label` ("12 rue X, 75000 Paris"),
// `properties.postcode`, `properties.city`, `properties.context`, etc.
const ADRESSE_GOUV_API = "https://api-adresse.data.gouv.fr/search/";

// Tiny inline component used to mark required fields with a visible red
// asterisk (spec utilisateur 2026-02). Placed after each <Label/>.
const RequiredMark = () => (
  <span aria-hidden="true" className="text-[#D7263D] font-bold ml-0.5" data-testid="required-asterisk">*</span>
);

export default function CommerceRegister() {
  const { registerCommerce } = useAuth();
  const [form, setForm] = useState({
    name: "", type: "", address: "", city: "", postalCode: "",
    email: "", password: "", phone: "", averageServiceTime: "",
    siret: "", rna: "", organizationType: "",
  });
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  // End-of-free-period date (admin-configurable). Loaded asynchronously
  // from /api/system/free-period. Initialized to null to avoid a brief
  // flash of the "jours offerts" banner while the API call is in flight.
  const [freePeriodEnd, setFreePeriodEnd] = useState(null);
  const [freePeriodLoaded, setFreePeriodLoaded] = useState(false);
  useEffect(() => {
    api.get("/system/free-period")
      .then((r) => { if (r.data?.endDate) setFreePeriodEnd(new Date(r.data.endDate)); })
      .catch(() => {})
      .finally(() => setFreePeriodLoaded(true));
  }, []);

  // === Anti-spam (fork 2026-02) ============================================
  // Simple arithmetic CAPTCHA + invisible honeypot + time-trap on submit.
  const [captcha, setCaptcha] = useState({ id: "", question: "Chargement…" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [hpWebsite, setHpWebsite] = useState(""); // honeypot (invisible)
  const formMountedAtMsRef = useRef(Date.now());

  // IP-block state — when set, the WHOLE form is hidden and only the
  // precise blocking message is shown to the user. `retryAfterSeconds`
  // is decremented every minute via a useEffect timer for a live countdown.
  const [ipBlock, setIpBlock] = useState({ blocked: false, message: "", retryAfterSeconds: 0 });

  // Live countdown : decrement every 60 s while blocked. When it hits 0,
  // we reload the page so the user transitions back to the normal form
  // (the server-side bucket has rolled over by then).
  useEffect(() => {
    if (!ipBlock.blocked) return;
    const tick = () => {
      setIpBlock((prev) => {
        if (!prev.blocked) return prev;
        const next = Math.max(0, (prev.retryAfterSeconds || 0) - 60);
        if (next <= 0) {
          // Cooldown elapsed → reload to re-probe the backend.
          setTimeout(() => window.location.reload(), 500);
        }
        return { ...prev, retryAfterSeconds: next };
      });
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [ipBlock.blocked]);

  // Formats remaining seconds into a human-readable countdown : "23h 58 min",
  // "1h 03 min", "12 min", "moins d'une minute".
  const formatRetryCountdown = (seconds) => {
    if (!seconds || seconds <= 0) return "moins d'une minute";
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return `${h} h`;
    return `${h} h ${String(m).padStart(2, "0")} min`;
  };

  const reloadCaptcha = async () => {
    try {
      const { data } = await api.get("/security/captcha");
      setCaptcha({ id: data.id, question: data.question });
      setCaptchaAnswer("");
    } catch {
      setCaptcha({ id: "", question: "Combien font 3 + 4 ?" });
    }
  };

  // Probe the server on mount to know if this IP is already blocked. When
  // blocked, do NOT load the CAPTCHA — there's nothing the user can do
  // until the cooldown elapses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/register/commerce/status");
        if (cancelled) return;
        if (data?.blocked) {
          setIpBlock({
            blocked: true,
            message: data.message || "Création temporairement indisponible.",
            retryAfterSeconds: Number(data.retryAfterSeconds) || 0,
          });
          return;
        }
        // Not blocked → load CAPTCHA + start the time-trap timer.
        reloadCaptcha();
        formMountedAtMsRef.current = Date.now();
      } catch {
        // On probe failure, fall back to normal flow (load CAPTCHA).
        if (!cancelled) {
          reloadCaptcha();
          formMountedAtMsRef.current = Date.now();
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // ==========================================================================

  // --- Address autocomplete (api-adresse.data.gouv.fr) -------------------
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressFocused, setAddressFocused] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);
  // Lat/lng kept aside — picked from the selected suggestion and sent to the
  // backend so the commerce shows up on the map / geo search.
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const nav = useNavigate();

  const fetchAddressSuggestions = (q) => {
    if (!q || q.trim().length < 3) {
      setAddressSuggestions([]);
      return;
    }
    setAddressLoading(true);
    fetch(`${ADRESSE_GOUV_API}?q=${encodeURIComponent(q)}&limit=6&type=housenumber`)
      .then((r) => r.json())
      .then((data) => {
        setAddressSuggestions(Array.isArray(data?.features) ? data.features : []);
      })
      .catch(() => setAddressSuggestions([]))
      .finally(() => setAddressLoading(false));
  };

  const onAddressChange = (val) => {
    setForm((f) => ({ ...f, address: val }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAddressSuggestions(val), 250);
  };

  const onPickSuggestion = (f) => {
    const p = f?.properties || {};
    const [lng, lat] = f?.geometry?.coordinates || [null, null];
    // Build a short "house number + street" label for the address field —
    // the postcode and city are filled separately so the visitor sees a
    // clean breakdown.
    const street = p.name || p.street || "";
    setForm((prev) => ({
      ...prev,
      address: street || p.label || "",
      postalCode: (p.postcode || prev.postalCode).toString().slice(0, 5),
      city: p.city || prev.city,
    }));
    setCoords({ lat, lng });
    setAddressSuggestions([]);
    setAddressFocused(false);
  };

  const validateSiret = (v) => !v || /^\d{14}$/.test(v.replace(/\s/g, ""));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validatePassword(form.password);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    if (form.password !== passwordConfirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!accepted) {
      setError("Veuillez accepter les conditions pour continuer");
      return;
    }
    if (form.postalCode && !/^\d{5}$/.test(form.postalCode)) {
      toast.error("Le code postal doit contenir exactement 5 chiffres.");
      return;
    }
    if (form.organizationType !== "association" && form.siret && !validateSiret(form.siret)) {
      toast.error("Le numéro SIRET doit contenir 14 chiffres.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        // Empty average service time → backend default (10 min)
        averageServiceTime: form.averageServiceTime
          ? Number(form.averageServiceTime)
          : 10,
        siret: form.organizationType === "association"
          ? null
          : (form.siret ? form.siret.replace(/\s/g, "") : null),
        rna: form.organizationType === "association" ? (form.rna || null) : null,
        isAssociation: form.organizationType === "association",
        organizationType: form.organizationType || null,
        phone: form.phone || null,
        lat: coords.lat,
        lng: coords.lng,
        termsAccepted: true,
        termsVersion: TERMS_VERSION,
        // === Anti-spam ===
        captchaId: captcha.id,
        captchaAnswer: captchaAnswer,
        hp_website: hpWebsite,
        formMountedAtMs: formMountedAtMsRef.current,
      };
      await registerCommerce(payload);
      toast.success("Établissement créé. Vérifiez votre email pour activer votre compte.");
      nav(`/check-email?email=${encodeURIComponent(form.email)}&role=commerce`, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || "Erreur";
      // If the backend just blocked this IP (429 on register) → switch the
      // whole UI to the blocked state so the user can't keep retrying with
      // a stale form.
      if (status === 429 && /trop de cr/i.test(msg)) {
        const retryAfter = Number(err.response?.headers?.["retry-after"]) || 0;
        setIpBlock({ blocked: true, message: msg, retryAfterSeconds: retryAfter });
        toast.error(msg);
      } else {
        setError(msg);
        toast.error(msg);
        // Refresh CAPTCHA on any error (single-use token may have been consumed)
        reloadCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-16 max-w-2xl">
      <Link to="/register" className="text-sm text-subtle hover:text-ink inline-flex items-center gap-1" data-testid="back-chooser-link">← Retour</Link>
      <p className="overline mb-2 mt-4">Établissement · Inscription</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">
        Créez votre file d'attente<br /><span className="text-brand">en 2 minutes.</span>
      </h1>
      {ipBlock.blocked ? (
        // === IP bloquée : on masque TOUT (formulaire + CAPTCHA + compteur)
        // et on affiche uniquement le message précis avec le temps restant.
        <div
          className="mt-10 border-2 border-[#D7263D] bg-[#FFF5F6] p-6 md:p-8 rounded-md"
          role="alert"
          data-testid="register-blocked-banner"
        >
          <div className="flex items-start gap-4">
            <span aria-hidden="true" className="text-3xl md:text-4xl shrink-0 leading-none">⛔</span>
            <div className="min-w-0">
              <p className="font-display font-black text-xl md:text-2xl text-[#D7263D] leading-tight">
                Création de compte temporairement bloquée
              </p>
              <p className="mt-2 text-sm md:text-base text-ink leading-relaxed" data-testid="register-blocked-message">
                {ipBlock.message}
              </p>
              {ipBlock.retryAfterSeconds > 0 && (
                <p
                  className="mt-3 inline-flex items-center gap-2 bg-white border border-[#D7263D]/30 text-[#D7263D] font-display font-black text-base md:text-lg px-3 py-2 rounded-md"
                  data-testid="register-blocked-countdown"
                >
                  <span aria-hidden="true">⏱️</span>
                  <span>
                    Vous pourrez réessayer dans <span data-testid="register-blocked-countdown-value">{formatRetryCountdown(ipBlock.retryAfterSeconds)}</span>
                  </span>
                </p>
              )}
              <p className="mt-4 text-xs md:text-sm text-subtle">
                Si vous pensez qu'il s'agit d'une erreur, vous pouvez nous contacter via la page support ou réessayer ultérieurement depuis un autre réseau.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center h-11 px-4 bg-ink hover:bg-brand text-white font-bold text-sm rounded-md transition-colors"
                  data-testid="register-blocked-contact-link"
                >
                  Contacter le support
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center h-11 px-4 border border-border hover:border-ink text-ink font-bold text-sm rounded-md transition-colors"
                  data-testid="register-blocked-home-link"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
      {/* Compteur dynamique : nombre de jours offerts restants jusqu'à la
         date FREE_UNTIL (config admin). Ne s'affiche qu'une fois la date
         chargée depuis /api/system/free-period pour éviter tout flash. */}
      {freePeriodLoaded && freePeriodEnd && (() => {
        const FREE_UNTIL = freePeriodEnd;
        const daysLeft = Math.max(0, Math.ceil((FREE_UNTIL - new Date()) / 86400000));
        const endLabel = FREE_UNTIL.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
        if (daysLeft <= 0) {
          return (
            <div className="mt-6 border border-border bg-surface/40 px-4 py-3 text-sm space-y-2" data-testid="register-plans-info">
              <p className="font-bold text-ink">Deux formules au choix, sans engagement&nbsp;:</p>
              <ul className="space-y-1.5 text-subtle">
                <li>
                  <span className="font-bold text-ink">Abonnement 9,90&nbsp;€/mois TTC</span>
                  <span> — 1ʳᵉ file d'attente en illimité, incluse.</span>
                </li>
                <li>
                  <span className="font-bold text-ink">Ponctuel 7,90&nbsp;€/file TTC</span>
                  <span> — une file ouverte pendant 24&nbsp;h, paiement à la demande.</span>
                </li>
              </ul>
              <p className="text-[11px] text-subtle italic">Files supplémentaires (2ᵉ, 3ᵉ…)&nbsp;: 4,90&nbsp;€/file TTC, valables 24&nbsp;h.</p>
            </div>
          );
        }
        return (
          <div
            className="mt-6 border border-[#0A7F2E]/30 bg-[#ECFBEF] px-4 md:px-5 py-3 md:py-4 flex items-start gap-3 md:gap-4"
            data-testid="register-free-counter"
          >
            <span aria-hidden="true" className="text-2xl md:text-3xl shrink-0">🎁</span>
            <div className="min-w-0">
              <p className="font-display font-black text-lg md:text-xl text-[#0A7F2E] leading-tight">
                <span data-testid="register-free-days" className="font-mono">{daysLeft}</span> jour{daysLeft > 1 ? "s" : ""} offert{daysLeft > 1 ? "s" : ""}&nbsp;!
              </p>
              <p className="text-xs md:text-sm text-ink/70 mt-0.5">
                Inscription <strong className="text-ink">totalement gratuite</strong> jusqu'au {endLabel}.
              </p>
              <p className="text-xs md:text-sm text-ink/70 mt-1.5 leading-snug">
                Au-delà, deux formules au choix sans engagement&nbsp;:<br />
                <span className="font-bold text-ink">9,90&nbsp;€/mois TTC</span> (1ʳᵉ file illimitée) ou <span className="font-bold text-ink">7,90&nbsp;€/file TTC</span> (ponctuel, 24&nbsp;h).
              </p>
            </div>
          </div>
        );
      })()}
      <form onSubmit={submit} className="mt-10 space-y-4" data-testid="commerce-register-form">
        {/* Hint en haut du formulaire : rappelle au visiteur que les champs
           marqués d'un astérisque rouge sont obligatoires. */}
        <p
          className="text-xs md:text-sm italic text-subtle border border-border bg-surface/40 px-3 py-2 rounded-md"
          data-testid="required-fields-hint"
        >
          Les champs marqués d'un astérisque <span className="text-[#D7263D] font-bold not-italic">*</span> sont obligatoires.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Nom de l'établissement<RequiredMark /></Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
              className="h-12 mt-1.5 uppercase"
              data-testid="name-input"
            />
          </div>
          <div>
            <Label>Type d'établissement<RequiredMark /></Label>
            <select required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`h-12 mt-1.5 w-full border border-input rounded-md px-3 text-sm bg-background ${form.type ? "" : "text-subtle"}`} data-testid="type-input">
              <option value="" disabled>Faites votre choix</option>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Address with live autocomplete (api-adresse.data.gouv.fr) */}
        <div className="relative">
          <Label>Adresse<RequiredMark /></Label>
          <Input
            required
            value={form.address}
            onChange={(e) => onAddressChange(e.target.value)}
            onFocus={() => setAddressFocused(true)}
            onBlur={() => {
              // Slight delay so a click on a suggestion still registers
              blurTimerRef.current = setTimeout(() => setAddressFocused(false), 180);
            }}
            placeholder="Commencez à taper votre adresse…"
            className="h-12 mt-1.5"
            autoComplete="off"
            data-testid="address-input"
          />
          {addressFocused && (addressSuggestions.length > 0 || addressLoading) && (
            <ul
              className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-border rounded-md shadow-lg text-sm"
              data-testid="address-suggestions"
            >
              {addressLoading && addressSuggestions.length === 0 && (
                <li className="px-3 py-2 text-subtle text-xs italic">Recherche…</li>
              )}
              {addressSuggestions.map((s) => (
                <li key={s?.properties?.id || s?.properties?.label}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); onPickSuggestion(s); }}
                    className="w-full text-left px-3 py-2 hover:bg-surface flex flex-col"
                    data-testid={`address-suggestion-${s?.properties?.id || s?.properties?.label}`}
                  >
                    <span className="font-medium text-ink leading-tight">
                      {s?.properties?.label || ""}
                    </span>
                    {s?.properties?.context && (
                      <span className="text-[11px] text-subtle leading-tight">{s.properties.context}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Postal code BEFORE city (per spec) — postal code is restricted to
            exactly 5 digits via maxLength + inputMode numeric + pattern. */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Code postal<RequiredMark /></Label>
            <Input
              required
              value={form.postalCode}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 5);
                setForm({ ...form, postalCode: onlyDigits });
              }}
              maxLength={5}
              inputMode="numeric"
              pattern="\d{5}"
              className="h-12 mt-1.5"
              data-testid="postal-input"
            />
          </div>
          <div>
            <Label>Ville<RequiredMark /></Label>
            <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-12 mt-1.5" data-testid="city-input" />
          </div>
        </div>

        <div>
          <Label>Email<RequiredMark /></Label>
          <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-1.5" data-testid="email-input" />
          {/* Spec fork 25.6 — suggestion de correction d'un domaine
             email manifestement mal orthographié. */}
          {(() => {
            const sug = suggestEmail(form.email);
            if (!sug) return null;
            return (
              <p
                className="mt-1.5 text-xs text-[#A65800] bg-[#FFF8EB] border border-[#F18F18]/40 rounded-md px-2 py-1.5 leading-snug"
                data-testid="email-typo-suggestion"
              >
                Vouliez-vous dire{" "}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, email: sug })}
                  className="font-bold text-brand underline-offset-2 hover:underline"
                  data-testid="email-typo-suggestion-apply"
                >
                  {sug}
                </button>{" "}
                ?
              </p>
            );
          })()}
        </div>

        {/* Password BELOW the email (per spec) — confirmation right after it. */}
        <div>
          <Label>Mot de passe<RequiredMark /></Label>
          <PasswordInput
            required
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            name="bpg-create-password"
            data-lpignore="true"
            data-form-type="other"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="h-12 mt-1.5"
            data-testid="password-input"
          />
          <CommercePasswordRules pwd={form.password} />
        </div>
        <div>
          <Label>Confirmer le mot de passe<RequiredMark /></Label>
          <PasswordInput
            required
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            name="bpg-create-password-confirm"
            data-lpignore="true"
            data-form-type="other"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className="h-12 mt-1.5"
            data-testid="password-confirm-input"
          />
          {passwordConfirm && form.password !== passwordConfirm && (
            <p className="mt-1 text-xs text-destructive" data-testid="password-confirm-error">
              Les mots de passe ne correspondent pas.
            </p>
          )}
        </div>

        {/* Spec utilisateur fork 25.9 + 25.11 — le champ « Temps moyen
           par visiteur » est remplacé par un champ « Téléphone »
           facultatif. Validation : 10 chiffres exactement, formatage
           automatique par paires (« 01 23 45 67 89 »). Pas de lettres
           autorisées. */}
        <div>
          <Label className="inline-flex items-center gap-1.5">
            Téléphone <span className="text-subtle font-normal">(facultatif)</span>
            <button
              type="button"
              onClick={() => toast.message(
                "Numéro public",
                {
                  description: "Si vous renseignez votre numéro de téléphone, il apparaîtra dans la recherche des visiteurs et sera visible par tout public. Vous pourrez le supprimer à tout moment depuis votre tableau de bord (icône corbeille).",
                  duration: 9000,
                },
              )}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#F18F18] hover:bg-[#F18F18]/10"
              aria-label="Informations sur la visibilité publique du numéro"
              data-testid="phone-warning-tooltip"
            >
              <AlertTriangle size={14} />
            </button>
          </Label>
          <Input
            type="tel"
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => {
              // On garde uniquement les chiffres (max 10) puis on
              // reformate par paires : « 01 23 45 67 89 ».
              const digits = (e.target.value || "").replace(/\D+/g, "").slice(0, 10);
              const formatted = digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
              setForm({ ...form, phone: formatted });
            }}
            placeholder="01 23 45 67 89"
            maxLength={14}
            className="h-12 mt-1.5"
            data-testid="phone-input"
          />
          <p className="mt-1.5 text-[11px] text-subtle leading-snug">
            10 chiffres — visible sur votre fiche publique si renseigné. Supprimable depuis le tableau de bord.
          </p>
        </div>

        <div className="flex items-start gap-3 border border-border p-3 bg-surface/50">
          <Checkbox
            id="terms"
            checked={accepted}
            onCheckedChange={(v) => { setAccepted(!!v); if (v) setError(""); }}
            className="mt-0.5"
            data-testid="terms-checkbox"
          />
          <label htmlFor="terms" className="text-sm text-ink leading-snug cursor-pointer">
            J'accepte les <Link to="/legal#cgu" target="_blank" className="underline text-brand">CGU</Link>, les <Link to="/legal#cgv" target="_blank" className="underline text-brand">CGV</Link>, les <Link to="/legal#mentions-legales" target="_blank" className="underline text-brand">Mentions légales</Link> et la <Link to="/legal#confidentialite" target="_blank" className="underline text-brand">Politique de confidentialité</Link>.
          </label>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="terms-error">{error}</p>}

        {/* === ANTI-SPAM (fork 2026-02) ============================================
           Champ honeypot invisible : si rempli → bot détecté côté backend.
           CAPTCHA arithmétique simple : `Combien font 3 + 5 ?`. */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
        >
          <label>
            Site Web (laissez vide)
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={hpWebsite}
              onChange={(e) => setHpWebsite(e.target.value)}
              data-testid="hp-website"
            />
          </label>
        </div>
        <div className="border border-border p-4 bg-surface/40 rounded-md">
          <Label htmlFor="captcha-answer" className="text-sm font-bold text-ink">
            Vérification de sécurité<RequiredMark />
          </Label>
          <div className="mt-2 flex items-center gap-3">
            <p className="text-sm text-ink flex-1" data-testid="captcha-question">
              {captcha.question}
            </p>
            <button
              type="button"
              onClick={reloadCaptcha}
              className="text-xs text-subtle hover:text-brand inline-flex items-center gap-1"
              data-testid="captcha-refresh-btn"
              aria-label="Régénérer la question"
            >
              <RefreshCw size={13} /> Régénérer
            </button>
          </div>
          <Input
            id="captcha-answer"
            type="text"
            inputMode="numeric"
            required
            value={captchaAnswer}
            onChange={(e) => setCaptchaAnswer(e.target.value)}
            placeholder="Votre réponse"
            className="h-11 mt-2 max-w-[200px]"
            data-testid="captcha-input"
          />
          <p className="mt-2 text-[11px] text-subtle">
            Cette étape confirme que vous n'êtes pas un robot.
          </p>
        </div>
        {/* === / ANTI-SPAM === */}

        <Button type="submit" disabled={loading} className="w-full h-14 bg-brand hover:bg-brand-hover rounded-md text-white font-medium text-base" data-testid="submit-btn">
          {loading ? "…" : "Créer mon espace établissement"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-subtle">
        Déjà inscrit ? <Link to="/commerce/login" className="text-brand font-medium">Se connecter</Link>
      </p>
      </>
      )}
      <WelcomePopup
        open={welcomeOpen}
        role="etablissement"
        onClose={() => { setWelcomeOpen(false); nav("/merchant/dashboard"); }}
      />
    </div>
  );
}

function CommercePasswordRules({ pwd }) {
  const r = passwordCheckBreakdown(pwd || "");
  const items = [
    { key: "length", label: "8 car. min", ok: r.length },
    { key: "letter", label: "1 lettre", ok: r.letter },
    { key: "digit", label: "1 chiffre", ok: r.digit },
    { key: "special", label: "1 spécial", ok: r.special },
  ];
  return (
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]" data-testid="password-rules">
        {items.map((it) => (
          <li
            key={it.key}
            className={`inline-flex items-center gap-1 transition-colors ${
              it.ok ? "text-[#0A7F2E] font-semibold" : "text-[#D7263D]"
            }`}
            data-rule-ok={it.ok ? "1" : "0"}
          >
            {it.ok ? <Check size={11} /> : <X size={11} />} {it.label}
          </li>
        ))}
      </ul>
  );
}
